import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import { UserContext } from "../App";

export default function Agency(props) {
  const { user } = useContext(UserContext);
  const isAdmin = user?.data[0]?.UserName === "admin";
  const [formData, setFormData] = useState({
    username: "",
    typeapp: "",
    commission_app: "",
  });

  const [responseMessage, setResponseMessage] = useState("");
  const [typeAppOptions, setTypeAppOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [searchUsername, setSearchUsername] = useState(""); // For search functionality
  const [isEditMode, setIsEditMode] = useState(false); // To determine if we're in edit mode
  const [editCommissionId, setEditCommissionId] = useState(null); // To track the commission being edited
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Fetch data from API
  useEffect(() => {
    if (!isAdmin) return;

    const fetchTypeApps = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/allapp`);
        setTypeAppOptions(response.data);
      } catch (error) {
        console.error("Error fetching type apps:", error);
      }
    };

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/get_all_info`);
        if (response.data && response.data.infos) {
          const formattedUsers = response.data.infos.map((user) => ({
            value: user.UserName,
            label: `${user.UserName}`,
          }));
          setUserOptions(formattedUsers);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchCommissions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/get_all_commissions`);
        setCommissionData(response.data);
      } catch (error) {
        console.error("Error fetching commission data:", error);
      }
    };

    fetchTypeApps();
    fetchUsers();
    fetchCommissions();
  }, [API_BASE_URL, isAdmin]);

  const handleChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode) {
        // Update existing commission
        const response = await axios.post(
          `${API_BASE_URL}/update_price_commission`,
          formData
        );
        setResponseMessage(`Success: ${response.data.message}`);
      } else {
        // Add new commission
        const response = await axios.post(`${API_BASE_URL}/add_commission`, formData);
        setResponseMessage(`Success: ${response.data.message}`);
      }

      // Reset the form and mode
      setFormData({
        username: "",
        typeapp: "",
        commission_app: "",
      });
      setIsEditMode(false);
      setEditCommissionId(null);
    } catch (error) {
      if (error.response) {
        setResponseMessage(`Error: ${error.response.data.error}`);
      } else {
        setResponseMessage("Error: Could not connect to the server.");
      }
      console.error("Error submitting form:", error);
    }
  };

  const handleEdit = (commission) => {
    // Set form data with the commission to edit
    setFormData({
      username: commission.UserName,
      typeapp: commission.Type_App,
      commission_app: commission.commission_App,
    });
    setIsEditMode(true);
    setEditCommissionId(commission.CommissionID); // Assuming 'CommissionID' is the unique identifier
  };

  const filteredCommissions = commissionData.filter((commission) =>
    commission.UserName.toLowerCase().includes(searchUsername.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger text-center">
          Bạn không có quyền truy cập trang cài đặt hoa hồng.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-1">
      {/* Title */}

      {/* Form Section */}
      <div className="card shadow-lg p-4 mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div className="mb-4">
              <label htmlFor="username" className="form-label font-weight-bold">
                Username
              </label>
              <Select
                id="username"
                name="username"
                options={userOptions}
                value={userOptions.find((option) => option.value === formData.username) || null}
                onChange={(selectedOption) => handleChange("username", selectedOption?.value || "")}
                placeholder="Select or search user"
                isClearable
                required
                className="react-select-container"
              />
            </div>

            {/* Type App */}
            <div className="mb-4">
              <label htmlFor="typeapp" className="form-label font-weight-bold">
                Type App
              </label>
              <select
                id="typeapp"
                name="typeapp"
                value={formData.typeapp}
                onChange={(e) => handleChange("typeapp", e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select App Type</option>
                {typeAppOptions.map((app) => (
                  <option key={app.Id_app} value={app.Id_app}>
                    {app.Name_app}
                  </option>
                ))}
              </select>
            </div>

            {/* Commission App */}
            <div className="mb-4">
              <label htmlFor="commission_app" className="form-label font-weight-bold">
                Commission App (%)
              </label>
              <input
                type="number"
                id="commission_app"
                name="commission_app"
                value={formData.commission_app}
                onChange={(e) => handleChange("commission_app", e.target.value)}
                className="form-control"
                placeholder="Enter commission percentage"
                step="0.01"
                min="0"
                max="100"
                required
              />
            </div>

            {/* Submit Button */}
            <div className="d-grid gap-2">
              <button type="submit" className="btn btn-primary py-3">
                {isEditMode ? "Update Commission" : "Submit Commission"}
              </button>
            </div>
          </form>

          {/* Response Message */}
          {responseMessage && (
            <div className="mt-4 alert alert-info text-center">
              {responseMessage}
            </div>
          )}
        </div>
      </div>

      {/* Search Bar for Commissions */}
      <div className="mb-4">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Username"
          value={searchUsername}
          onChange={(e) => setSearchUsername(e.target.value)}
        />
      </div>

      {/* Commission Data Table */}
      <div className="card shadow-lg p-4">
        <div className="card-body">
          <h3 className="mb-4 text-dark">Commission Data</h3>
          <table className="table table-striped table-bordered">
            <thead className="table-light">
              <tr>
                <th>Username</th>
                <th>App Type</th>
                <th>Commission (%)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.length > 0 ? (
                filteredCommissions.map((commission) => (
                  <tr key={commission.CommissionID}>
                    <td>{commission.UserName}</td>
                    <td>{commission.Type_App}</td>
                    <td>{commission.commission_App}%</td>
                    <td>
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handleEdit(commission)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">No commissions found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
